import React, { useRef, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { StyleSheet, View } from 'react-native';

import { MarketSelectorElementDefinition } from '../../types/FormBuilder/ElementDefinitions';
import { MarketDates } from '../../types/shared/Vendor';
import MarketSelectorFormView from '../FormBuilder/FormElements/MarketSelectorElement/MarketSelectorFormView';
import { Checkbox } from '../uiold';
import { LoadingIndicator, ApolloError } from '../../core-ui';
import { useSeason } from '../SeasonState';

import {
  GetMarketData,
  GetMarketDataVariables,
} from './__generated__/GetMarketData';

const GET_MARKET_DATA = gql`
  query GetMarketData($seasonId: String!) {
    season(id: $seasonId) {
      id
      signupForm {
        fields {
          id
          type
          definition
        }
      }
      markets {
        id
        name
        description
        dates
        numSpots
        hidden
        createdAt
        updatedAt
      }
    }
  }
`;

type Props = {
  selectedMarketDates: Set<string>;
  onSelectionChange: (value: Set<string>) => void;
  signupFeeOnly: boolean;
  onSignupFeeOnlyChange: (value: boolean) => void;
};

export default function MarketDatesPanel(props: Props) {
  let {
    onSelectionChange,
    selectedMarketDates,
    signupFeeOnly,
    onSignupFeeOnlyChange,
  } = props;

  const { season } = useSeason();

  const { data, loading, error } = useQuery<
    GetMarketData,
    GetMarketDataVariables
  >(GET_MARKET_DATA, {
    variables: { seasonId: season?.id ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const { markets, fieldDefinition } = useMemo(() => {
    const season = data?.season;
    const markets = season?.markets.map((market) => {
      const { id, numSpots, ...other } = market;
      // TODO: Find a way to remove the need for `season` field
      return { id, season: season.id, num_spots: numSpots, ...other };
    });
    const field = season?.signupForm?.fields.find((field) => {
      return field.type === 'MarketSelectorElement';
    });
    const parsed = field ? JSON.parse(field.definition) : undefined;
    const fieldDefinition = parsed
      ? ((parsed as unknown) as MarketSelectorElementDefinition)
      : undefined;
    return { markets, fieldDefinition };
  }, [data]);

  const marketSelectorRef = useRef<MarketSelectorFormView>(null);

  if (error) {
    return <ApolloError error={error} />;
  }

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (!data.season || !markets || !fieldDefinition) {
    return <p>{t('Season not found')}</p>;
  }

  return (
    <>
      <View>
        <MarketSelectorFormView
          definition={fieldDefinition}
          markets={markets}
          invoiceView={true}
          handleMarketDateSelect={(marketDates) => {
            onSelectionChange(fromMarketDates(marketDates));
          }}
          value={toMarketDates(selectedMarketDates)}
          ref={marketSelectorRef}
          hideLabel={true}
        />
      </View>
      <View style={styles.section}>
        <Checkbox
          text={t('Invoice sign up/membership fee only')}
          isChecked={signupFeeOnly}
          onChange={(checked) => {
            if (checked) {
              marketSelectorRef.current?.selectAll();
            }
            onSignupFeeOnlyChange(checked);
          }}
        />
      </View>
    </>
  );
}

function toMarketDates(input: Set<string>) {
  const results: MarketDates = {};
  for (const selection of input) {
    const [marketId = '', date = ''] = selection.split('|');
    const dates = results[marketId] ?? (results[marketId] = []);
    dates.push(date);
  }
  return results;
}

function fromMarketDates(input: MarketDates) {
  const results = new Set<string>();
  for (const [marketId, dates] of Object.entries(input)) {
    for (const date of dates) {
      results.add(marketId + '|' + date);
    }
  }
  return results;
}

const styles = StyleSheet.create({
  section: {
    marginHorizontal: 30,
    marginVertical: 5,
    fontSize: 18,
  },
});
